import { useEffect } from "react";
import { useRoute } from "wouter";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { EdgeGlow } from "@/components/edge-glow";
import { SectionDivider } from "@/components/landing-page-components/section-divider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { type Story } from "@shared/schema";

export default function StoryPage() {
  const [_, params] = useRoute("/story/:id");
  const storyId = params?.id;
  
  // Fetch the story from the API
  const { data: story, isLoading, error } = useQuery<Story>({
    queryKey: [`/api/stories/${storyId}`],
    enabled: !!storyId,
  });

  // Format the date 
  const formatStoryDate = (dateString: Date | null) => {
    if (!dateString) return 'Unknown date';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return 'May 2025';
    }
  };

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, [storyId]);

  if (isLoading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#14b8a6]"></div>
      </div>
    );
  }
  
  if (error || !story) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center p-6">
        <h2 className="text-2xl font-bold mb-4">Story not found</h2>
        <p className="text-gray-400 mb-8">We couldn't find the story you're looking for.</p>
        <Link href="/voices">
          <Button className="bg-[#14b8a6] hover:bg-[#0f9488] text-black font-semibold">
            Return to Stories
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen relative">
      <EdgeGlow />
      <Navbar />
      
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-16 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link href="/voices">
            <Button variant="ghost" className="text-[#14b8a6] hover:bg-[#14b8a6]/10 mb-6 -ml-2 px-2">
              <ChevronLeft size={20} className="mr-1" /> Back to Stories
            </Button>
          </Link>
          
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge variant="outline" className="bg-[#14b8a6]/10 text-[#14b8a6] border-[#14b8a6]/30">
              {story.categoryId === 1 ? "Patient Story" : "Healthcare"}
            </Badge>
            <span className="text-gray-400 text-sm">
              {formatStoryDate(story.createdAt)} · {story.readTime || "5 min read"}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {story.title}
          </h1>
          
          <div className="flex items-center space-x-3 mb-8">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-[#14b8a6]/20 text-[#14b8a6]">
                {story.authorInitials || (story.authorName ? story.authorName.split(' ').map((n: string) => n[0]).join('') : 'EL')}
              </AvatarFallback>
            </Avatar>
            <div className="text-gray-300">{story.authorName || 'Anonymous'}</div>
          </div>
        </motion.div>
        
        <motion.div 
          className="prose prose-invert max-w-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Story excerpt from database */}
          <p className="text-xl text-gray-300 italic mb-6">
            {story.excerpt}
          </p>
          
          {/* Main content from database */}
          <div className="text-gray-300 mb-6 whitespace-pre-wrap">
            {story.content}
          </div>
          
          {/* Extract a quote from the story content itself */}
          <div className="border-l-4 border-[#14b8a6] pl-6 py-2 mb-10">
            <p className="text-xl text-[#14b8a6] font-semibold italic">
              {/* Pull a quote directly from the story's excerpt */}
              "{story.excerpt}"
            </p>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 pt-8 border-t border-gray-800"
        >
          <h3 className="text-2xl font-bold mb-6">Share Your Story</h3>
          <p className="text-gray-300 mb-6">
            Your experience matters. If you've had a similar experience or want to share your healthcare journey, 
            we'd love to hear from you.
          </p>
          
          <Link href="/voices#submit-story">
            <Button className="bg-[#14b8a6] hover:bg-[#0f9488] text-black font-semibold px-6 py-4">
              Submit Your Story
            </Button>
          </Link>
        </motion.div>
      </section>
      
      <SectionDivider />
      <Footer />
    </div>
  );
}